import React, { useState, useEffect, useRef } from 'react';
import { submitForm } from '../utils/formSubmit';
import Swal from 'sweetalert2';
import './FreeTrialModal.css';

const UsFlag = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 650 390" 
    style={{ borderRadius: '2px', display: 'inline-block', verticalAlign: 'middle', width: '22px', height: '14px', flexShrink: 0 }}
  >
    <rect width="650" height="390" fill="#b22234"/>
    <path d="M0 30h650M0 90h650M0 150h650M0 210h650M0 270h650M0 330h650" stroke="#fff" strokeWidth="30"/>
    <rect width="260" height="210" fill="#3c3b6e"/>
    <g fill="#fff">
      <circle cx="26" cy="21" r="9"/>
      <circle cx="78" cy="21" r="9"/>
      <circle cx="130" cy="21" r="9"/>
      <circle cx="182" cy="21" r="9"/>
      <circle cx="234" cy="21" r="9"/>
      <circle cx="52" cy="52.5" r="9"/>
      <circle cx="104" cy="52.5" r="9"/>
      <circle cx="156" cy="52.5" r="9"/>
      <circle cx="208" cy="52.5" r="9"/>
      <circle cx="26" cy="84" r="9"/>
      <circle cx="78" cy="84" r="9"/>
      <circle cx="130" cy="84" r="9"/>
      <circle cx="182" cy="84" r="9"/>
      <circle cx="234" cy="84" r="9"/>
      <circle cx="52" cy="115.5" r="9"/>
      <circle cx="104" cy="115.5" r="9"/>
      <circle cx="156" cy="115.5" r="9"/>
      <circle cx="208" cy="115.5" r="9"/>
      <circle cx="26" cy="147" r="9"/>
      <circle cx="78" cy="147" r="9"/>
      <circle cx="130" cy="147" r="9"/>
      <circle cx="182" cy="147" r="9"/>
      <circle cx="234" cy="147" r="9"/>
      <circle cx="52" cy="178.5" r="9"/>
      <circle cx="104" cy="178.5" r="9"/>
      <circle cx="156" cy="178.5" r="9"/>
      <circle cx="208" cy="178.5" r="9"/>
    </g>
  </svg>
);

const countryList = [
  { name: 'United States', code: '+1', iso: 'us' }
];

const FreeTrialModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    course: 'Noorani Qaida'
  });

  const [selectedCountry, setSelectedCountry] = useState(countryList[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const modalRef = useRef(null);
  const countryDropdownRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    const handleClickOutside = (e) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitForm('Modal Free Trial Booking', {
        fullName: formData.fullName,
        email: formData.email,
        phone: `${selectedCountry.code} ${formData.phone}`,
        course: formData.course
      });

      Swal.fire({
        title: 'Trial Request Sent!',
        text: `Thank you, ${formData.fullName}! Your free trial request has been submitted.`,
        icon: 'success',
        confirmButtonColor: '#006100'
      });

      setFormData({
        fullName: '',
        phone: '',
        email: '',
        course: 'Noorani Qaida'
      });
      onClose();
      window.location.href = '/thank-you-enrollment-successful/';
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: 'Failed to send trial request. Please try again.',
        icon: 'error',
        confirmButtonColor: '#006100'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCountries = countryList.filter(
    (c) =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.code.includes(searchQuery)
  );

  return (
    <div className="trial-modal-overlay" onClick={onClose}>
      <div 
        className="trial-modal-card" 
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button 
          className="trial-modal-close" 
          onClick={onClose}
          aria-label="Close modal"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <form onSubmit={handleSubmit} className="trial-modal-form">
          {/* Full Name Input */}
          <div className="trial-modal-group">
            <input
              type="text"
              name="fullName"
              placeholder="Full Name"
              value={formData.fullName}
              onChange={handleChange}
              required
              className="trial-modal-input"
            />
          </div>

          {/* Interactive Phone Input with Country Flag Dropdown */}
          <div className="trial-modal-group" ref={countryDropdownRef} style={{ position: 'relative' }}>
            <div className="trial-modal-phone-box">
              <button
                type="button"
                className="trial-modal-country-picker-btn"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <UsFlag />
                <span className="trial-modal-arrow">▲</span>
                <span className="trial-modal-code">{selectedCountry.code}</span>
              </button>
              <span className="trial-modal-divider">|</span>
              <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handleChange}
                required
                className="trial-modal-phone-input"
              />
            </div>

            {/* Country Dropdown Menu */}
            {isDropdownOpen && (
              <div className="trial-modal-country-dropdown">
                <div className="trial-modal-search-wrapper">
                  <input
                    type="text"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="trial-modal-search-input"
                    autoFocus
                  />
                </div>
                <ul className="trial-modal-country-menu">
                  {filteredCountries.map((c) => (
                    <li
                      key={c.name}
                      className={`trial-modal-country-option ${c.name === selectedCountry.name ? 'trial-modal-country-option--active' : ''}`}
                      onClick={() => {
                        setSelectedCountry(c);
                        setIsDropdownOpen(false);
                        setSearchQuery('');
                      }}
                    >
                      <span className="trial-modal-opt-flag">
                        <UsFlag />
                      </span>
                      <span className="trial-modal-opt-name">{c.name}</span>
                      <span className="trial-modal-opt-code">{c.code}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Email Address Input */}
          <div className="trial-modal-group">
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleChange}
              required
              className="trial-modal-input"
            />
          </div>

          {/* Course Select Dropdown */}
          <div className="trial-modal-group">
            <select
              name="course"
              value={formData.course}
              onChange={handleChange}
              required
              className="trial-modal-select"
            >
              <option value="Noorani Qaida">Noorani Qaida</option>
              <option value="Quran Tajweed Classes">Quran Tajweed Classes</option>
              <option value="Quran Reading Classes">Quran Reading Classes</option>
              <option value="Quran Memorization Classes">Quran Memorization Classes</option>
              <option value="Quran Translation Classes">Quran Translation Classes</option>
            </select>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="trial-modal-submit-btn"
          >
            {submitting ? 'Booking Trial...' : 'Book My Free Trial'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default FreeTrialModal;
